import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { KeywordDraft } from "./messaging-rules";

export type ConnectionStatus = "pending" | "connected" | "error" | "revoked";

/** The church's text-messaging keyword (`configuration.opt_in_keyword`, set by `set_messaging_keyword`). */
export interface MessagingKeyword {
  readonly keyword: string;
  readonly confirmWord: string;
  readonly welcome: string;
  readonly confirmation: string;
  readonly joinUrl: string;
}

/** The church's Twilio connection: its non-secret configuration only; the auth token never leaves Vault. */
export interface MessagingConnection {
  readonly id: string;
  readonly status: ConnectionStatus;
  readonly from: string | null;
  readonly messagingServiceSid: string | null;
  readonly keyword: MessagingKeyword | null;
  readonly lastError: string | null;
}

const text = (value: unknown): string | null => (typeof value === "string" ? value : null);

function readKeyword(value: unknown): MessagingKeyword | null {
  if (typeof value !== "object" || value === null) return null;
  const k = value as Record<string, unknown>;
  const keyword = text(k["keyword"]);
  if (keyword === null) return null;
  return {
    keyword,
    confirmWord: text(k["confirm_word"]) ?? "YES",
    welcome: text(k["welcome"]) ?? "",
    confirmation: text(k["confirmation"]) ?? "",
    joinUrl: text(k["join_url"]) ?? "",
  };
}

function useTenantId(): string {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : "";
}

export function useMessagingConnection(enabled: boolean) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["messaging-connection", tenantId],
    enabled: enabled && tenantId !== "",
    queryFn: async (): Promise<MessagingConnection | null> => {
      const { data, error } = await supabase
        .from("tenant_integrations")
        .select("id, status, configuration, last_error")
        .eq("tenant_id", tenantId)
        .eq("provider_key", "twilio")
        .maybeSingle();
      if (error) throw error;
      if (data === null) return null;
      const configuration = (data.configuration ?? {}) as Record<string, unknown>;
      return {
        id: data.id,
        status: data.status,
        from: text(configuration["from"]),
        messagingServiceSid: text(configuration["messaging_service_sid"]),
        keyword: readKeyword(configuration["opt_in_keyword"]),
        lastError: data.last_error,
      };
    },
  });
}

/** After any change, the card reads the connection again. */
function useRefresh() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return () => client.invalidateQueries({ queryKey: ["messaging-connection", tenantId] });
}

export interface ConnectDetails {
  readonly accountSid: string;
  readonly token: string;
  readonly from: string;
  readonly messagingServiceSid: string;
}

export function useConnectTwilio() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (details: ConnectDetails) => {
      const sid = details.messagingServiceSid.trim();
      const { error } = await supabase.rpc("connect_integration", {
        p_tenant_id: tenantId,
        p_provider_key: "twilio",
        p_configuration: {
          from: details.from.trim(),
          account_sid: details.accountSid.trim(),
          ...(sid === "" ? {} : { messaging_service_sid: sid }),
        },
        p_secret: details.token,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: refresh,
  });
}

export function useDisconnectTwilio() {
  const supabase = useSupabase();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (connectionId: string) => {
      const { error } = await supabase.rpc("disconnect_integration", {
        p_tenant_integration_id: connectionId,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: refresh,
  });
}

/** Sets the Messaging Service, or removes it with null (`set_messaging_service`, #320). */
export function useSetMessagingService() {
  const supabase = useSupabase();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (input: { connectionId: string; sid: string | null }) => {
      const { error } = await supabase.rpc("set_messaging_service", {
        p_tenant_integration_id: input.connectionId,
        // The generated type says string; the function takes null to remove it.
        p_messaging_service_sid: input.sid as string,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: refresh,
  });
}

/** Sets the text-to-join keyword, or removes it with null (`set_messaging_keyword`, #319). */
export function useSetKeyword() {
  const supabase = useSupabase();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (input: { connectionId: string; draft: KeywordDraft | null }) => {
      const { draft } = input;
      const { error } = await supabase.rpc("set_messaging_keyword", {
        p_tenant_integration_id: input.connectionId,
        // As above: null removes the keyword.
        p_keyword: (draft === null ? null : draft.keyword.trim()) as string,
        ...(draft === null
          ? {}
          : {
              p_welcome: draft.welcome,
              p_confirm_word: draft.confirmWord.trim(),
              p_confirmation: draft.confirmation,
              ...(draft.joinUrl.trim() === "" ? {} : { p_join_url: draft.joinUrl.trim() }),
            }),
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: refresh,
  });
}
